/**
 * PhoneCallDialog: the one Phone Call container, shared by the Dashboard (`DashboardView`) and Today
 * (`TodaysAppointmentsPage`). Main's drawer host, unchanged in behaviour: the DS `PageScaffoldDrawer` (scrim click to
 * dismiss, `role="dialog" aria-modal`) holding the DS `Panel` (title bar, close, Escape), with `useFocusTrap` for focus
 * in on open and back to the trigger on close. The host marks everything else `inert` while it is open (its own page,
 * and the rail through `usePhoneCallDrawerStore` in AppShell), so the body-portaled patient search stays reachable.
 *
 * Showcase look (design TodayPhoneCallScreen): drawn as the centred 560 modal over a blurred window by
 * `.phone-call-modal` / `.phone-call-panel` (showcase-auth.css; the scrim and dialog are `position: fixed`, so the
 * dialog's place in the DOM does not matter). The DS Modal is not used: its Base UI dialog hides and dismisses on the
 * body-portaled patient search (dashboard-view.test.tsx B9, commit aba1df22).
 */
import { useRef, type ReactNode } from 'react';
import { Panel, PageScaffoldDrawer } from '@scripsteam/scrips-signal-ds';
import { useFocusTrap } from '@/shared/hooks/use-focus-trap';

export interface PhoneCallDialogProps {
  open: boolean;
  onClose: () => void;
  /** The capture panel (PhoneCallPanel), rendered while open. */
  children: ReactNode;
}

export function PhoneCallDialog({ open, onClose, children }: PhoneCallDialogProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, open);
  return (
    <PageScaffoldDrawer ref={drawerRef} open={open} onDismiss={onClose} aria-label="Phone Call" className="phone-call-modal">
      <Panel title="Phone Call" onClose={onClose} width="md" className="phone-call-panel">
        {children}
      </Panel>
    </PageScaffoldDrawer>
  );
}
